/** Help mode catalogue + runtime types. See docs/decisions/2026-10-17-in-app-help-mode.md. */

/** Every text has an `en` field; a `bn` field can be added later without code changes. */
export interface LocalText { en: string; bn?: string }

export type HelpLang = 'en' | 'bn';

export const pickText = (t: LocalText | undefined, lang: HelpLang = 'en'): string => (t ? (t[lang] ?? t.en) : '');

/** One entry per `data-help` key (a button, field, column header, chip...). */
export interface HelpEntry {
    title: LocalText;
    /** What it does. */
    what: LocalText;
    /** When and why to use it, in factory terms. */
    business: LocalText;
    /** What happens next: next flow step, accounting/stock effect, or "no accounting entry yet". */
    next?: LocalText;
    /** Permission keys (from constants/permissions). Holding ANY ONE of them lets the user use it. */
    keys?: string[];
    /** True when the user needs EVERY key in `keys` together, not just one (e.g. a cash sale needs invoice and receipt rights). */
    needsAllKeys?: boolean;
    /** Walkthrough link (absolute http(s) URL or an in-app path). */
    link?: string;
}

/** One entry per `data-help-section` / `data-help-page` key. */
export interface HelpSectionEntry extends HelpEntry {
    /** How the features in the section connect, e.g. "Change the discount, VAT and the total recalculate". */
    connects?: LocalText;
}

/** What a catalogue file `client/src/help/<module>.ts` default-exports. */
export interface HelpModule {
    elements: Record<string, HelpEntry>;
    /** Sections AND pages (`data-help-section` / `data-help-page`). */
    sections: Record<string, HelpSectionEntry>;
}

export const defineHelp = (m: HelpModule): HelpModule => m;

export interface HelpCatalogue {
    elements: Record<string, HelpEntry>;
    sections: Record<string, HelpSectionEntry>;
}

/**
 * The reason a screen already computed when it disabled a control. The card only READS it;
 * it never re-checks anything on its own.
 */
export type DisabledReason =
    | { kind: 'missing-key'; keys?: string[] }   // keys default to the entry's own keys
    | { kind: 'status'; message: string }         // e.g. "Only a draft can be edited"
    | { kind: 'module-off'; module?: string }
    | { kind: 'plan-limit'; message?: string }
    | { kind: 'custom'; message: string };

export interface DisabledInfo { reason: DisabledReason | null }
export interface FeatureRef { key: string; el: HTMLElement; entry: HelpEntry; disabled: DisabledInfo | null }
export interface SectionRef { key: string; el: HTMLElement; entry: HelpSectionEntry }
export interface Box { left: number; top: number; right: number; bottom: number }

export type HelpTarget =
    | { kind: 'element'; key: string; el: HTMLElement; entry: HelpEntry; disabled: DisabledInfo | null; section: SectionRef | null }
    | { kind: 'area'; via: 'box' | 'section' | 'fallback-section' | 'page'; section: SectionRef | null; features: FeatureRef[]; box?: Box }
    | { kind: 'missing'; key: string | null };

/** A resolved target plus the catalogue keys that were tagged in the page but have no text yet. */
export interface Resolution { target: HelpTarget; missingKeys: string[] }

/** Lookup of role NAMES that hold permission keys. Never people. */
export interface RoleDirectory { rolesHolding(keys: string[]): Promise<string[]> }

/** Missing-help log sink. Key + normalised page path only. No backend table is built in H0. */
export interface HelpLogger { logMissing(entry: { key: string | null; path: string }): void }
